/**
 * Рекомендации: что сделать, чтобы называли чаще.
 *
 * Спека §9 задаёт три вещи, и все три видны на экране.
 *
 * 1. **Активных не больше двенадцати.** Растущий список превращается в свалку и
 *    перестаёт открываться, поэтому счётчик активных стоит рядом с заголовком, а
 *    не прячется в настройках.
 * 2. **Часть советов продукт проверяет сам** — доступ роботов, разметка,
 *    страница вопросов и ответов, карта сайта. Такие закрываются машинной
 *    проверкой, и руками состояние `auto_closed` не выставляется: иначе история
 *    закрытий перестаёт быть доказательством.
 * 3. **История накапливается**, а не переписывается при каждом замере: клиент
 *    должен видеть, что сделал за квартал. Поэтому закрытые не исчезают, а
 *    уходят под фильтр.
 *
 * Связка «сделали → цифра выросла» — главный довод за продление, и именно
 * поэтому эффект показывается честно: он доказателен только при одной ревизии
 * ядра в обоих окнах и полных сетках, иначе на экране стоит причина, а не
 * стрелка вверх.
 */

import { useState } from 'react';

import { api, idempotencyKey } from '../api/client';
import { useAccess } from '../session';
import { WithBrand } from '../ui/screen';
import { useRead, type Page } from '../ui/query';
import { Segments } from '../ui/controls';
import { ErrorNotice, Empty, Skeleton } from '../ui/state';
import { Pill } from '../ui/viz';
import {
  NOT_MEASURED,
  dateProse,
  deltaPoints,
  percent,
  period as periodFmt,
} from '@shared/src/format';
import { SEVERITY_WORDS } from '@shared/src/product';
import type { components } from '../api/schema';

type Recommendation = components['schemas']['Recommendation'];
type RecommendationDetail = components['schemas']['RecommendationDetail'];
type RecommendationStatus = components['schemas']['RecommendationStatus'];
type RecommendationCategory = components['schemas']['RecommendationCategory'];
type Priority = components['schemas']['Priority'];

const STATUS: Record<RecommendationStatus, string> = {
  open: 'Не начато',
  in_progress: 'В работе',
  done: 'Сделано',
  auto_closed: 'Закрыто проверкой',
  dismissed: 'Отклонено',
};

const CATEGORY: Record<RecommendationCategory, string> = {
  technical: 'Техника сайта',
  content: 'Содержание',
  sources: 'Источники',
  reputation: 'Репутация',
  local: 'Локальное присутствие',
};

/**
 * Важность — одна лестница на весь продукт, и слова у неё общие с
 * оповещениями: `SEVERITY_WORDS` в `shared/src/product.ts`. Здесь стоял свой
 * словарь — «Высокий · Средний · Низкий», — и получалось, что одна и та же
 * ступень называется «Важно» на бейдже оповещения и «Высокий» в советах.
 * Продукт, который просит читать важность так, как её присвоило правило,
 * обязан называть её одинаково.
 */
const PRIORITY: Record<Priority, string> = SEVERITY_WORDS;

type FilterKey = 'active' | 'done' | 'all';

const FILTERS = [
  // Фильтр включает и «Не начато», поэтому «В работе» называл не то, что
  // отбирал: под ним лежат все незакрытые советы.
  { value: 'active' as const, label: 'Активные' },
  { value: 'done' as const, label: 'Закрытые' },
  { value: 'all' as const, label: 'Все' },
];

const OPEN_STATES: RecommendationStatus[] = ['open', 'in_progress'];

export function Recommendations() {
  return (
    <WithBrand title="Рекомендации">{(brandId) => <Body brandId={brandId} />}</WithBrand>
  );
}

function Body({ brandId }: { brandId: string }) {
  const access = useAccess();
  const [filter, setFilter] = useState<FilterKey>('active');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const list = useRead<Page<Recommendation>>(`/brands/${brandId}/recommendations`, {
    limit: 50,
  });

  const canEdit = access.reaches('PATCH', '/recommendations/{recommendationId}');
  const canGenerate = access.reaches(
    'POST',
    '/brands/{brandId}/recommendations/generate',
  );

  if (list.kind === 'failed') return <ErrorNotice error={list.error} />;
  if (list.kind === 'loading') return <Skeleton rows={3} />;

  const all = list.data.items;
  const active = all.filter((item) => OPEN_STATES.includes(item.status));
  const closed = all.filter((item) => !OPEN_STATES.includes(item.status));
  const shown = filter === 'active' ? active : filter === 'done' ? closed : all;

  const change = (item: Recommendation, status: RecommendationStatus) => {
    setBusy(true);
    setFailure(null);
    api
      .patch<Recommendation>(`/recommendations/${item.id}`, { status })
      .then((updated) =>
        list.put({
          ...list.data,
          items: all.map((one) => (one.id === updated.id ? updated : one)),
        }),
      )
      .catch(setFailure)
      .finally(() => setBusy(false));
  };

  const regenerate = () => {
    setBusy(true);
    setFailure(null);
    api
      .post<Recommendation[]>(`/brands/${brandId}/recommendations/generate`, undefined, {
        idempotencyKey: idempotencyKey(),
      })
      .then((items) => list.put({ ...list.data, items }))
      .catch(setFailure)
      .finally(() => setBusy(false));
  };

  return (
    <>
      <div className="filters">
        <Segments
          label="Что показать"
          options={FILTERS}
          value={filter}
          onChange={setFilter}
        />
        {canGenerate ? (
          <button
            className="btn btn-primary btn-sm"
            type="button"
            onClick={regenerate}
            disabled={busy}
          >
            Пересобрать список
          </button>
        ) : null}
      </div>

      {failure ? <ErrorNotice error={failure} /> : null}

      {shown.length === 0 ? (
        <Empty
          eyebrow="Рекомендации"
          title={
            filter === 'done'
              ? 'Закрытых рекомендаций пока нет'
              : 'Активных рекомендаций нет'
          }
          action={
            canGenerate ? (
              <button
                className="btn btn-ghost"
                type="button"
                onClick={regenerate}
                disabled={busy}
              >
                Пересобрать список
              </button>
            ) : undefined
          }
        >
          {filter === 'done'
            ? 'Как только совет будет выполнен или закрыт машинной проверкой, он появится здесь — история не переписывается при каждом замере.'
            : 'После каждого замера мы пересобираем советы сами. Если список пуст, а замер был, значит закрывать нечего.'}
        </Empty>
      ) : (
        <div className="items">
          {shown.map((item) => (
            <Item
              key={item.id}
              item={item}
              canEdit={canEdit}
              busy={busy}
              onChange={change}
            />
          ))}
        </div>
      )}
    </>
  );
}

function Item({
  item,
  canEdit,
  busy,
  onChange,
}: {
  item: Recommendation;
  canEdit: boolean;
  busy: boolean;
  onChange: (item: Recommendation, status: RecommendationStatus) => void;
}) {
  const [open, setOpen] = useState(false);
  const closed = !OPEN_STATES.includes(item.status);

  return (
    <article
      className={`item${item.priority === 'critical' ? ' critical' : item.priority === 'high' ? ' high' : ''}`}
    >
      <div className="item-mark">
        <b>{PRIORITY[item.priority]}</b>
        {CATEGORY[item.category]}
        <span className="stamp">{STATUS[item.status]}</span>
      </div>

      <div className="item-body">
        <h3>{item.title}</h3>
        {item.body ? <p>{item.body}</p> : null}

        {item.action_items && item.action_items.length > 0 ? (
          <ul>
            {item.action_items.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ul>
        ) : null}

        <span className="stamp">
          {item.impact_estimate !== null && item.impact_estimate !== undefined
            ? `Ожидаемый прирост видимости ${deltaPoints(item.impact_estimate, 1)}. `
            : 'Прирост заранее не оцениваем: у этого совета нет сопоставимых наблюдений. '}
          {item.check_code
            ? 'Выполнение мы проверим сами и закроем совет без вашего участия.'
            : 'Отметить выполнение придётся руками: машинной проверки на это нет.'}
        </span>

        {item.status === 'auto_closed' ? (
          <span className="stamp">
            Закрыто машинной проверкой {item.closed_at ? dateProse(item.closed_at) : ''}.
            Вручную такое не поставить.
          </span>
        ) : null}

        {item.close_reason ? (
          <span className="stamp">Причина: {item.close_reason}</span>
        ) : null}

        {closed ? (
          <>
            <button
              className="linkish"
              type="button"
              aria-expanded={open}
              onClick={() => setOpen((now) => !now)}
            >
              {open ? 'Свернуть' : 'Что это дало'}
            </button>
            {open ? <Effect id={item.id} /> : null}
          </>
        ) : null}
      </div>

      {/* Роль, которая ничего не меняет, не видит серых кнопок: их здесь нет
          вовсе (app-kit.md §1). */}
      {canEdit && !closed ? (
        <div className="item-act">
          {item.status === 'open' ? (
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              disabled={busy}
              onClick={() => onChange(item, 'in_progress')}
            >
              Взять в работу
            </button>
          ) : null}
          <button
            className="btn btn-ink btn-sm"
            type="button"
            disabled={busy}
            onClick={() => onChange(item, 'done')}
          >
            {/* Контрол называется по результату, а не по состоянию: «Сделано» —
                это надпись на бейдже статуса, и та же надпись на кнопке рядом
                читается как «уже сделано», а не «отметить». Проза этого экрана
                глагол уже выбрала: «Отметить выполнение придётся руками». */}
            Отметить выполненным
          </button>
          {/* Отказ от совета не разрушает данные, поэтому и не выглядит
              разрушительным: `.btn-danger` в этой системе — про удаление. */}
          <button
            className="btn btn-ghost btn-sm"
            type="button"
            disabled={busy}
            onClick={() => onChange(item, 'dismissed')}
          >
            Отклонить совет
          </button>
        </div>
      ) : null}
    </article>
  );
}

/**
 * Эффект рекомендации. Доказателен только при одной ревизии ядра в обоих окнах
 * и полных сетках — иначе на экране стоит причина, по которой сравнивать нельзя.
 */
function Effect({ id }: { id: string }) {
  const detail = useRead<RecommendationDetail>(`/recommendations/${id}`);

  if (detail.kind === 'loading') return <Skeleton rows={1} />;
  if (detail.kind === 'failed') return <ErrorNotice error={detail.error} />;

  const effect = detail.data.effect;
  const checks = detail.data.checks ?? [];

  if (!effect) {
    return (
      <p className="stamp">
        Окно «до и после» ещё не набралось. Эффект появится, когда после закрытия пройдёт
        полный замер.
      </p>
    );
  }

  return (
    <>
      <div className="effect">
        <div>
          <b>До</b>
          <span className="tnum">
            {effect.visibility_before === null || effect.visibility_before === undefined
              ? NOT_MEASURED
              : percent(effect.visibility_before, 1)}
          </span>
          <p className="stamp">{periodFmt(effect.before_start, effect.before_end)}</p>
        </div>
        <div>
          <b>После</b>
          <span className="tnum">
            {effect.visibility_after === null || effect.visibility_after === undefined
              ? NOT_MEASURED
              : percent(effect.visibility_after, 1)}
          </span>
          <p className="stamp">{periodFmt(effect.after_start, effect.after_end)}</p>
        </div>
        <div>
          <b>Разница</b>
          <span>
            <Pill
              value={effect.is_valid ? effect.delta_pp : null}
              text={
                effect.is_valid &&
                effect.delta_pp !== null &&
                effect.delta_pp !== undefined
                  ? deltaPoints(effect.delta_pp, 1)
                  : 'сравнивать нельзя'
              }
            />
          </span>
        </div>
      </div>

      {!effect.is_valid ? (
        <p className="stamp">
          {effect.invalid_reason ??
            'В окнах «до» и «после» разное семантическое ядро или неполные сетки. Разница в таком сравнении говорила бы не о рекомендации.'}
        </p>
      ) : null}

      {checks.length > 0 ? (
        <p className="stamp">
          Машинные проверки:{' '}
          {checks
            .map(
              (check) =>
                `${check.check_code} — ${
                  check.result === 'passed'
                    ? 'прошла'
                    : check.result === 'failed'
                      ? 'не прошла'
                      : 'проверить не удалось'
                }`,
            )
            .join(' · ')}
          . «Проверить не удалось» рекомендацию не закрывает.
        </p>
      ) : null}
    </>
  );
}
